import { useMemo } from 'react';

import { flatten } from 'utils/flatten';

import messagesInEnglish from '../../lang/en.json';
import messagesInEnglishGB from '../../lang/en-GB.json';
import messagesInEnglishCA from '../../lang/en-CA.json';
import messagesInEnglishIE from '../../lang/en-IE.json';
import messagesInEnglishAU from '../../lang/en-AU.json';
import messagesInGerman from '../../lang/de-DE.json';
import messagesInSpanish from '../../lang/es-ES.json';
import messagesInFrenchCA from '../../lang/fr-CA.json';
import messagesInFrench from '../../lang/fr-FR.json';
import messagesInItalian from '../../lang/it-IT.json';
import messagesInNorwegian from '../../lang/nb-NO.json';
import messagesInDutch from '../../lang/nl-NL.json';
import messagesInPolish from '../../lang/pl.json';
import messagesInPortuguese from '../../lang/pt-PT.json';
import messagesInHungarian from '../../lang/hu-HU.json';
import messagesInSlovak from '../../lang/sk-SK.json';

export const DEFAULT_LOCALE = 'en';

// Locale keys are lowercase-hyphenated; getAvailableLocale normalizes user input
// (lowercases + converts underscores to hyphens) before lookup, so a canonical tag
// (e.g. `fr-FR`) resolves — see VIS-1695. Bare-language aliases (de, es, fr, it, nb/no,
// nl, pt, hu, sk) map region-less locales onto a catalogue.
const messagesMap = new Map<string, Record<string, unknown>>([
  ['en', messagesInEnglish.ecoBee],
  ['en-gb', messagesInEnglishGB.ecoBee],
  ['en-ca', messagesInEnglishCA.ecoBee],
  ['en-ie', messagesInEnglishIE.ecoBee],
  ['en-au', messagesInEnglishAU.ecoBee],
  ['de-de', messagesInGerman.ecoBee],
  ['de', messagesInGerman.ecoBee],
  ['es-es', messagesInSpanish.ecoBee],
  ['es', messagesInSpanish.ecoBee],
  ['fr-ca', messagesInFrenchCA.ecoBee],
  ['fr-fr', messagesInFrench.ecoBee],
  ['fr', messagesInFrench.ecoBee],
  ['it-it', messagesInItalian.ecoBee],
  ['it', messagesInItalian.ecoBee],
  ['nb-no', messagesInNorwegian.ecoBee],
  ['nb', messagesInNorwegian.ecoBee],
  ['no', messagesInNorwegian.ecoBee],
  ['nl-nl', messagesInDutch.ecoBee],
  ['nl', messagesInDutch.ecoBee],
  ['pl', messagesInPolish.ecoBee],
  ['pt-pt', messagesInPortuguese.ecoBee],
  ['pt', messagesInPortuguese.ecoBee],
  ['hu-hu', messagesInHungarian.ecoBee],
  ['hu', messagesInHungarian.ecoBee],
  ['sk-sk', messagesInSlovak.ecoBee],
  ['sk', messagesInSlovak.ecoBee],
]);

// A structurally invalid tag reaches Intl only via the bare-language fallback: an input like
// `de_` or `de__AT` normalizes to `de-` / `de--at`, which still matches the `de` alias but is not
// a usable BCP-47 tag, so `Intl.NumberFormat(locale)` would throw a RangeError at render time.
// Keep the full normalized tag only when Intl can consume it; otherwise fall back to the bare
// language, which always can.
const isIntlSafe = (tag: string): boolean => {
  try {
    return Boolean(new Intl.NumberFormat(tag));
  } catch {
    return false;
  }
};

export const getAvailableLocale = (userLocale: string): [string, string] => {
  const normalized = userLocale.toLowerCase().replace(/_/g, '-');

  if (messagesMap.has(normalized)) {
    return [normalized, normalized];
  }

  const [lang] = normalized.split('-');

  if (messagesMap.has(lang)) {
    return [lang, isIntlSafe(normalized) ? normalized : lang];
  }

  return [DEFAULT_LOCALE, DEFAULT_LOCALE];
};

interface LocaleData {
  messages: Record<string, string>;
  locale: string;
}

export const useLocaleData = (userLocale: string): LocaleData => useMemo(() => {
  const [messagesKey, locale] = getAvailableLocale(userLocale);

  return {
    locale,
    messages: flatten(messagesMap.get(messagesKey)) as Record<string, string>,
  };
}, [userLocale]);
